import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, SimpleChanges, OnChanges, OnInit, OnDestroy } from '@angular/core';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { BreakpointService } from 'app/modules/breakpoint/breakpoint.service';
import { DropdownWithCount, CategoryNode } from '../../../models/allProduct.interface';
import { Subscription } from 'rxjs';

type TriState = 'checked' | 'indeterminate' | 'unchecked';

@Component({
  selector: 'multi-select-dropdown',
  templateUrl: './multi-select-dropdown.component.html',
  animations: [
    trigger('dropdownMultiselectState', [
      state('inactive', style({ height: '0', maxHeight: '0'})),
      state('active', style({ height: '*', maxHeight: '240px' })),
      transition('inactive => active', animate('200ms ease-in')),
      transition('active => inactive', animate('200ms ease-in')),
    ]),
  ],
  standalone: false,
})
export class MultiSelectDropdownComponent implements OnInit, OnChanges, OnDestroy {
  @Input() buttonTitle = 'Filter';
  @Input() size: 'default' | 'small' = 'default';
  @Input() showCounts = false;
  @Input() showClearButton = false;
  @Input() dropdownContent: DropdownWithCount[] = [];
  @Input() isHierarchicalData = false;
  @Input() tree: CategoryNode | null = null;
  @Input() autoOpen = false;
  @Input() breakpointActive = true;
  @Input() showHierarchicalCount = false;

  @Output() optionSelected = new EventEmitter<DropdownWithCount | { clearAll: true }>();
  @Output() selectionChange = new EventEmitter<Set<string>>();
  @Output() cleared = new EventEmitter<void>();

  @ViewChild('dropdownFilterOption') dropdownFilterOption: ElementRef<HTMLElement>;

  state: 'active' | 'inactive' = 'inactive';
  active = false;
  bigScreen = true;
  selectionCount = 0;

  private readonly ALL_CATEGORIES_ID = 'All Categories';
  private nodeStateCache = new Map<string, TriState>();
  readonly ROOT_ID = '__root_categories__';
  selectedLeafIds = new Set<string>();

  shouldScroll = false;
  private readonly MAX_SCROLL_HEIGHT = 200;
  private readonly dropdownId = `dropdown-${Math.random().toString(36).substring(2, 11)}`;
  private breakpointSubscription?: Subscription;
  private dropdownManagerSubscription?: Subscription;

  constructor(private breakpointService: BreakpointService) {}

  ngOnInit(): void {
    this.breakpointSubscription = this.breakpointService.getBreakpointObservable().subscribe((bp) => {
      this.bigScreen = !['xs', 'sm', 'md', undefined].includes(bp);

      if (this.isHierarchicalData && this.autoOpen && this.breakpointActive) {
        this.active = true;
        this.state = 'active';
      }

      this.updateFlatSelectionCount();
      if (this.active) {
        setTimeout(() => this.evaluateScroll(), 0);
      }
    });

    this.dropdownManagerSubscription = this.breakpointService.closeAllDropdowns$.subscribe((excludeId) => {
      if (excludeId !== this.dropdownId && this.active) {
        this.active = false;
        this.state = 'inactive';
        this.shouldScroll = false;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dropdownContent'] && !this.isHierarchicalData) {
      this.dropdownContent = (this.dropdownContent || []).map((i) => ({ ...i, checked: !!i.checked }));
      this.updateFlatSelectionCount();
      if (this.active) setTimeout(() => this.evaluateScroll(), 0);
    }

    if (changes['tree'] && this.isHierarchicalData) {
      this.invalidateNodeStateCache();
    }

    if (changes['isHierarchicalData']) {
      this.active = false;
      this.state = 'inactive';
      this.invalidateNodeStateCache();
      this.selectedLeafIds.clear();
    }

    if (changes['breakpointActive'] && this.isHierarchicalData) {
      if (this.breakpointActive && this.autoOpen) {
        this.active = true;
        this.state = 'active';
      } else if (!this.breakpointActive) {
        this.active = false;
        this.state = 'inactive';
      }
    }
  }

  toggleState(event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (!this.active) {
      this.breakpointService.closeOtherDropdowns(this.dropdownId);
    }

    this.active = !this.active;
    this.state = this.active ? 'active' : 'inactive';

    if (this.active) {
      setTimeout(() => this.evaluateScroll(), 0);
    } else {
      this.shouldScroll = false;
    }
  }

  get clearDisabled(): boolean {
    return this.isHierarchicalData
      ? this.selectedLeafIds.size === 0
      : this.selectionCount === 0;
  }

  onClearClick(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    if (this.clearDisabled) return;
    if (this.isHierarchicalData) {
      this.clearHierarchical(event);
    } else {
      this.clearFlatSelection(event);
    }
  }

  onOptionChange(content: DropdownWithCount, ev: Event): void {
    if (this.isHierarchicalData) return;
    const input = ev.target as HTMLInputElement;
    content.checked = input.checked;
    this.updateFlatSelectionCount();
    this.optionSelected.emit({ name: content.name, checked: content.checked, count: content.count });
  }

  clearFlatSelection(event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.dropdownContent.forEach((i) => (i.checked = false));
    this.updateFlatSelectionCount();
    this.optionSelected.emit({ clearAll: true });
    this.cleared.emit();
  }

  private updateFlatSelectionCount(): void {
    this.selectionCount = this.dropdownContent.filter((i) => i.checked).length;
  }

  isNodeChecked(node: CategoryNode): boolean {
    return this.getNodeTriState(node) === 'checked';
  }

  isNodeIndeterminate(node: CategoryNode): boolean {
    return this.getNodeTriState(node) === 'indeterminate';
  }

  onHierarchicalToggle(node: CategoryNode, event: Event): void {
    event.stopPropagation();
    this.invalidateNodeStateCache();
    if (this.isRoot(node)) return;

    if (this.isAllCategories(node)) {
      this.selectedLeafIds.clear();
      this.invalidateNodeStateCache();
      this.emitHierarchicalSelection();
      this.cleared.emit();
      return;
    }

    const prev = this.getNodeTriState(node);

    if (this.isLeaf(node)) {
      this.selectedLeafIds.has(node.id) ? this.selectedLeafIds.delete(node.id) : this.selectedLeafIds.add(node.id);
      this.invalidateNodeStateCache();
      this.emitHierarchicalSelection();
      return;
    }

    const descendants = this.getDescendantLeafNodeIds(node);
    if (prev === 'checked') {
      descendants.forEach((id) => this.selectedLeafIds.delete(id));
    } else {
      descendants.forEach((id) => this.selectedLeafIds.add(id));
    }
    this.invalidateNodeStateCache();
    this.emitHierarchicalSelection();
  }

  clearHierarchical(event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.selectedLeafIds.clear();
    this.invalidateNodeStateCache();
    this.emitHierarchicalSelection();
    this.cleared.emit();
  }

  private emitHierarchicalSelection(): void {
    this.selectionChange.emit(new Set(this.selectedLeafIds));
  }

  private getNodeTriState(node: CategoryNode): TriState {
    const cacheKey = node.id;
    const cached = this.nodeStateCache.get(cacheKey);
    if (cached) return cached;

    let computed: TriState;
    if (this.isAllCategories(node)) {
      const allLeafIds = this.getAllLeafNodeIds(this.tree);
      const allSelected = allLeafIds.length > 0 && allLeafIds.every((id) => this.selectedLeafIds.has(id));
      computed = allSelected ? 'checked' : this.selectedLeafIds.size === 0 ? 'unchecked' : 'indeterminate';
      this.nodeStateCache.set(cacheKey, computed);
      return computed;
    }

    if (this.isLeaf(node)) {
      computed = this.selectedLeafIds.has(node.id) ? 'checked' : 'unchecked';
      this.nodeStateCache.set(cacheKey, computed);
      return computed;
    }

    const childStates = (node.children || []).map((c) => this.getNodeTriState(c));
    const allChecked = childStates.length > 0 && childStates.every((s) => s === 'checked');
    const noneChecked = childStates.every((s) => s === 'unchecked');
    computed = allChecked ? 'checked' : noneChecked ? 'unchecked' : 'indeterminate';
    this.nodeStateCache.set(cacheKey, computed);
    return computed;
  }

  private invalidateNodeStateCache(): void {
    this.nodeStateCache.clear();
  }

  private isRoot(node: CategoryNode): boolean {
    return node.id === this.ROOT_ID;
  }

  private isAllCategories(node: CategoryNode): boolean {
    return node.id === this.ALL_CATEGORIES_ID;
  }

  private isLeaf(node: CategoryNode): boolean {
    return !node.children || node.children.length === 0;
  }

  private getAllLeafNodeIds(root: CategoryNode | null): string[] {
    if (!root) return [];
    const leaves: string[] = [];
    const visit = (n: CategoryNode) => {
      if (this.isLeaf(n) && !this.isRoot(n) && !this.isAllCategories(n)) {
        leaves.push(n.id);
      } else {
        (n.children || []).forEach(visit);
      }
    };
    visit(root);
    return leaves;
  }

  private getDescendantLeafNodeIds(node: CategoryNode): string[] {
    if (this.isLeaf(node)) return this.isAllCategories(node) ? [] : [node.id];
    const collected: string[] = [];
    (node.children || []).forEach((c) => collected.push(...this.getDescendantLeafNodeIds(c)));
    return collected.filter((id) => id !== this.ALL_CATEGORIES_ID);
  }

  clickedOutside(): void {
    this.active = false;
    this.state = 'inactive';
    this.shouldScroll = false;
  }

  private evaluateScroll(): void {
    if (!this.active || this.isHierarchicalData) {
      this.shouldScroll = false;
      return;
    }
    const host = this.dropdownFilterOption?.nativeElement.querySelector('.content-padding') as HTMLElement;
    if (!host) {
      this.shouldScroll = false;
      return;
    }
    this.shouldScroll = host.scrollHeight > this.MAX_SCROLL_HEIGHT;
  }

  selectOption(value: string): boolean {
    const item = this.dropdownContent.find((i) => i.name === value && !i.checked);
    if (item) {
      item.checked = true;
      this.updateFlatSelectionCount();
      this.optionSelected.emit({ name: item.name, checked: true, count: item.count });
      return true;
    }
    return false;
  }

  deselectOption(value: string): boolean {
    const item = this.dropdownContent.find((i) => i.name === value && i.checked);
    if (item) {
      item.checked = false;
      this.updateFlatSelectionCount();
      this.optionSelected.emit({ name: item.name, checked: false, count: item.count });
      return true;
    }
    return false;
  }

  deselectAllOption(): void {
    this.clearFlatSelection();
  }

  ngOnDestroy(): void {
    this.breakpointSubscription?.unsubscribe();
    this.dropdownManagerSubscription?.unsubscribe();
  }
}
